import React, { useEffect, useId, useRef } from "react";
import { cn } from "../../utils/cn";
import {
  createBrushPainter,
  screenFrame,
  SEMO_MOTION_ASPECT,
  SEMO_MOTION_VIEWBOX,
  stillFrame,
} from "./semoMotionEngine";

// 화면이 열리는 동안 로딩이 «로그인 확인 → 화면 받기 → 데이터 받기» 로 갈아 끼워져도
// 움직임이 처음부터 다시 시작하지 않도록 모든 로딩이 시계 하나를 같이 쓴다.
// 마지막 로딩이 사라지고 잠시(RESET_AFTER_MS) 아무것도 없으면 다음 로딩은 처음부터 시작한다.
const RESET_AFTER_MS = 400;
let clockStart = 0;
let mounted = 0;
let lastUnmount = -Infinity;
function acquireClock(): void {
  const now = performance.now();
  if (mounted === 0 && now - lastUnmount > RESET_AFTER_MS) clockStart = now;
  mounted++;
}
function releaseClock(): void {
  mounted = Math.max(0, mounted - 1);
  lastUnmount = performance.now();
}

export interface SemoMotionLoaderProps extends React.HTMLAttributes<HTMLDivElement> {
  /** 높이(px). 너비는 심벌 비율(약 2:3)로 정해진다. */
  size?: number;
}

/**
 * 세모 로고 로딩 — 원 두 개가 시차를 두고 위에서 아래로 빠르게 지나가며 두 획을 그리고, 꼬리가 지워지는 사이
 * 다음 원들이 다시 그려 내려온다. 끊기는 순간 없이 준비될 때까지 이어진다.
 * 준비되면 화면이 이 컴포넌트를 치우는 순간 중간이라도 끊긴다.
 * 시스템에서 «동작 줄이기»를 켠 사람에게는 완성된 심벌을 멈춘 채 보여 준다.
 */
export function SemoMotionLoader({
  size = 64,
  className,
  style,
  ...props
}: SemoMotionLoaderProps) {
  const layerRef = useRef<SVGGElement>(null);
  const prefix = `semo-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}-`;

  useEffect(() => {
    const layer = layerRef.current;
    if (!layer) return;
    let raf = 0;
    acquireClock();

    const paint = createBrushPainter(layer, prefix);
    const reduced =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced || typeof requestAnimationFrame !== "function") {
      paint(stillFrame());
    } else {
      const tick = (now: number) => {
        paint(screenFrame(Math.max(0, now - clockStart) / 1000));
        raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    }

    return () => {
      releaseClock();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [prefix]);

  return (
    <div
      className={cn("flex shrink-0 items-center justify-center", className)}
      style={{ width: size * SEMO_MOTION_ASPECT, height: size, ...style }}
      data-semo-loader=""
      {...props}
    >
      <svg
        viewBox={SEMO_MOTION_VIEWBOX}
        width="100%"
        height="100%"
        aria-hidden="true"
        focusable="false"
      >
        <g ref={layerRef} />
      </svg>
    </div>
  );
}
